import { CSSProperties, ReactNode, TouchEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { registerModalBackHandler } from '../../../lib/native';
import { viewportHeight } from '../../../lib/viewport';

export interface PhotoLightboxProps {
  images: string[];
  /** Index into `images` to open on. */
  startIndex: number;
  onClose: () => void;
  /** Alt prefix for photos, e.g. "Matheus's photo". */
  alt?: string;
}

const OPEN_MS = 280;
const CLOSE_MS = 220;
const DISMISS_MS = 200;
const DISMISS_THRESHOLD = 110;
// Matches UIKit's default sheet-presentation spring — the curve iOS uses to
// open/dismiss modals, so this reads as the same motion as the OS chrome
// around it rather than a generic CSS "ease".
const SPRING = 'cubic-bezier(.32,.72,0,1)';
// An accelerating "ease-in" for the commit-to-dismiss finish, since that
// motion is leaving the screen rather than settling into it.
const EASE_IN = 'cubic-bezier(.4,0,1,1)';

/**
 * PhotoLightbox — full-screen photo viewer opened from a PhotoGrid cell.
 *
 * Between-photo paging reuses the exact mechanism as the host-card carousel
 * (native `scroll-snap-type: x mandatory`, one slide per finger position)
 * instead of custom touch-delta math — that's what makes it track the
 * finger 1:1 with real momentum/rubber-banding, the thing a hand-rolled
 * swipe threshold can't cheaply reproduce.
 *
 * Vertical drag on the photo area (not a horizontal swipe) live-follows the
 * finger to dismiss, iOS Photos-style: past DISMISS_THRESHOLD it completes
 * the close (shrinking to nothing, not just fading), short of it it springs
 * back. The two gestures are disambiguated by whichever axis the first
 * ~6px of movement commits to.
 *
 * The live drag mutates `contentRef`/`backdropRef` styles directly instead
 * of going through React state — a `setState` per `touchmove` forces a full
 * re-render every ~16ms, which is real, visible lag on a page this size.
 * It also sidesteps a subtler bug the state-driven version had: disabling
 * the transition during a drag by string-building `transform ${'none'}`
 * produces the invalid declaration `transition: transform none`, which
 * browsers silently drop — so the drag was still animating through the
 * 280ms open transition on every frame instead of jumping with the finger.
 * Direct ref mutation never touches `transition` unintentionally like that.
 */
export function PhotoLightbox({ images, startIndex, onClose, alt = 'Photo' }: PhotoLightboxProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(startIndex);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const hasMultiple = images.length > 1;

  const gesture = useRef<{ x: number; y: number; axis: 'x' | 'y' | null } | null>(null);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollLeft = startIndex * el.clientWidth;
  }, [startIndex]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf = 0;
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const w = el!.clientWidth;
        if (w > 0) setIndex(Math.round(el!.scrollLeft / w));
      });
    }
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const requestClose = useCallback(() => {
    setOpen(false);
    setClosing(true);
    setTimeout(onClose, CLOSE_MS);
  }, [onClose]);

  // Android hardware back button closes the lightbox instead of navigating
  // the route underneath it.
  useEffect(() => {
    if (closing) return;
    return registerModalBackHandler(requestClose);
  }, [closing, requestClose]);

  function goTo(i: number) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: Math.max(0, Math.min(images.length - 1, i)) * el.clientWidth, behavior: 'smooth' });
  }

  function onTouchStart(e: TouchEvent) {
    const t = e.touches[0];
    gesture.current = { x: t.clientX, y: t.clientY, axis: null };
  }

  function onTouchMove(e: TouchEvent) {
    const g = gesture.current;
    if (!g) return;
    const t = e.touches[0];
    const dx = t.clientX - g.x;
    const dy = t.clientY - g.y;
    if (!g.axis) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      // Only a downward drag hands off to dismiss — an upward or
      // horizontal-dominant move stays with native scroll-snap paging.
      g.axis = Math.abs(dy) > Math.abs(dx) && dy > 0 ? 'y' : 'x';
      if (g.axis === 'y' && contentRef.current && backdropRef.current) {
        // No queued transition to fight — every frame from here jumps
        // straight to the new value instead of easing toward it.
        contentRef.current.style.transition = 'none';
        backdropRef.current.style.transition = 'none';
      }
    }
    if (g.axis === 'y') {
      const dyClamped = Math.max(0, dy);
      const scale = Math.max(0.4, 1 - dyClamped / 500);
      const opacity = Math.max(0.25, 1 - dyClamped / 380);
      if (contentRef.current) contentRef.current.style.transform = `translateY(${dyClamped}px) scale(${scale})`;
      if (backdropRef.current) backdropRef.current.style.opacity = String(opacity);
    }
  }

  function onTouchEnd(e: TouchEvent) {
    const g = gesture.current;
    gesture.current = null;
    if (g?.axis !== 'y') return;
    const t = e.changedTouches[0];
    const dy = t ? Math.max(0, t.clientY - g.y) : 0;
    if (dy > DISMISS_THRESHOLD) {
      // Deliberately NOT routed through the `open`/`closing` state: a
      // setState here would re-render with the declarative
      // scale(.92)/opacity-0 styles and stomp the in-progress ref-driven
      // transform below before the timeout below ever fires. The parent
      // unmounts this component via `onClose` once the animation below
      // finishes, so React never needs to know about this transition.
      if (contentRef.current) {
        contentRef.current.style.transition = `transform ${DISMISS_MS}ms ${EASE_IN}`;
        contentRef.current.style.transform = `translateY(${dy + 200}px) scale(0)`;
      }
      if (backdropRef.current) {
        backdropRef.current.style.transition = `opacity ${DISMISS_MS}ms ${EASE_IN}`;
        backdropRef.current.style.opacity = '0';
      }
      setTimeout(onClose, DISMISS_MS);
    } else if (contentRef.current && backdropRef.current) {
      contentRef.current.style.transition = `transform 260ms ${SPRING}`;
      contentRef.current.style.transform = 'scale(1)';
      backdropRef.current.style.transition = `opacity 260ms ${SPRING}`;
      backdropRef.current.style.opacity = '1';
    }
  }

  const backdropOpacity = open && !closing ? 1 : 0;
  const contentTransform = !open || closing ? 'scale(.92)' : 'scale(1)';
  const motionTransition = `${closing ? CLOSE_MS : OPEN_MS}ms ${SPRING}`;

  return (
    <div
      ref={backdropRef}
      role="dialog"
      aria-label={`${alt} ${index + 1} of ${images.length}`}
      onClick={requestClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 80,
        background: 'rgba(0,0,0,.94)',
        opacity: backdropOpacity,
        transition: `opacity ${motionTransition}`,
      }}
    >
      <PressButton ariaLabel="Close" onPress={requestClose} style={{ position: 'absolute', top: 'max(16px, var(--sat))', right: 16 }}>
        <i className="ti ti-x" />
      </PressButton>

      {hasMultiple && (
        <span
          style={{
            position: 'absolute', top: 'max(16px, var(--sat))', left: 16, zIndex: 1,
            fontFamily: 'var(--font-sans)', fontSize: 12.5, color: 'rgba(255,255,255,.7)',
          }}
        >
          {index + 1} / {images.length}
        </span>
      )}

      <div
        ref={contentRef}
        style={{
          position: 'absolute', inset: 0, transform: contentTransform, transition: `transform ${motionTransition}`,
          // Vertical panning is handled entirely by our own touch handlers
          // (drag-to-dismiss) rather than the browser's default scroll —
          // without this, iOS still tries to rubber-band the page behind
          // this fixed overlay on a vertical touch, fighting our own
          // transform frame-for-frame. Horizontal stays native so the
          // scroll-snap paging below keeps its real momentum.
          touchAction: 'pan-x',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          ref={scrollerRef}
          className="flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ position: 'absolute', inset: 0 }}
        >
          {images.map((src, i) => (
            <div
              key={i}
              className="snap-center"
              style={{ flex: '0 0 100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={src}
                alt={`${alt} ${i + 1}`}
                style={{ maxWidth: '92vw', maxHeight: viewportHeight(0.82), objectFit: 'contain', borderRadius: 4 }}
              />
            </div>
          ))}
        </div>
      </div>

      {hasMultiple && (
        <PressButton
          ariaLabel="Previous photo"
          onPress={() => goTo(index - 1)}
          style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40,
            ...(index === 0 ? { opacity: 0.25, pointerEvents: 'none' } : null),
          }}
        >
          <i className="ti ti-chevron-left" />
        </PressButton>
      )}

      {hasMultiple && (
        <PressButton
          ariaLabel="Next photo"
          onPress={() => goTo(index + 1)}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40,
            ...(index === images.length - 1 ? { opacity: 0.25, pointerEvents: 'none' } : null),
          }}
        >
          <i className="ti ti-chevron-right" />
        </PressButton>
      )}
    </div>
  );
}

/** A round glass button with an iOS-style press dim/scale — no CSS :active
 *  needed since inline styles can't express it, so press state is tracked
 *  directly. */
function PressButton({ ariaLabel, onPress, style, children }: { ariaLabel: string; onPress: () => void; style?: CSSProperties; children: ReactNode }) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={(e) => { e.stopPropagation(); onPress(); }}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        zIndex: 1, width: 36, height: 36, borderRadius: 999, border: 'none', cursor: 'pointer',
        background: 'rgba(255,255,255,.12)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
        transform: pressed ? 'scale(.86)' : 'scale(1)',
        opacity: pressed ? 0.8 : 1,
        transition: 'transform .12s ease, opacity .12s ease',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export default PhotoLightbox;
